import { GOES_ALLOWED, MASTERMIND_SHAPES, type MastermindSize } from '@hazel/shared'

/**
 * One board, one table.
 *
 * The rule Sky's Memory Match learned the hard way and Stripey's Sudoku wrote
 * down: the number of places the cursor steps along and the class that draws
 * them live together, and a test checks they still agree. Her board's cursor
 * kept its own idea of the width while the CSS widened at a breakpoint, so
 * Down moved four cells across a six-wide row.
 *
 * **What is different here is the height.** Every other board is as tall as it
 * is wide, or as tall as its content; this one is always ten rows of history
 * plus the row she is building, whatever size she picked. That is not a
 * layout detail — it is the game. `clueLayout.ts` puts it best about Rocky's:
 * for a logic puzzle, *seeing everything at once is the task*, because
 * deduction means holding constraints together. A history she has to scroll
 * back through is a memory test wearing a deduction puzzle's clothes.
 *
 * So the sizes here are measured against 1080 rather than chosen, and the
 * numbers below are what a television was seen to fit.
 */

export interface MastermindLayout {
  /** Places in a row. Also what the cursor steps along. */
  readonly pegs: number
  /** How many teddies she picks from. */
  readonly palette: number
  /** The row of guesses, and the peg buttons in it. */
  readonly rowClassName: string
  /** One peg, at the ordinary scales. */
  readonly pegClassName: string
  /** One peg on the television, where ten rows have to fit 1080. */
  readonly tvPegClassName: string
}

/*
 * Four pegs and five are drawn at the same size.
 *
 * The tempting thing is to shrink the five-peg board because it is wider, and
 * it would be wrong: what bounds this board is its *height*, which is ten rows
 * either way. A five-peg row is wider than a four-peg one and a 16:9 screen
 * has width to spare — so shrinking it would give back nothing and cost her
 * the thing she is trying to tell apart.
 */
const PEG = 'h-10 w-10 sm:h-12 sm:w-12'
const TV_PEG = '[[data-scale=tv]_&]:h-14 [[data-scale=tv]_&]:w-14'

/**
 * One row of the board, whether it has been played or is being built.
 *
 * Three columns, and the outer two are `1fr` each so they always split
 * whatever is left over **equally** — which puts the pegs dead centre of the
 * board in every row, whatever is beside them.
 *
 * That is the whole point of it. The rows she has played carry a number on the
 * left and an answer on the right; the row she is building carries neither. Laid
 * out by their own widths they each centre *themselves*, so the pegs she is
 * choosing sat some ninety pixels to the right of the pegs she is comparing
 * them against — in a game whose entire method is reading down a column to see
 * what changed.
 *
 * Equal-share columns rather than fixed widths, because a fixed width is a
 * number that has to be right at three scales and wrong at none of them, and
 * the answer beside it grows from 14px to 32px between a phone and a
 * television. `1fr` is the same arrangement at every size without anybody
 * having measured anything.
 *
 * Shared from here rather than written at both call sites: two copies of a
 * grid template is how the two halves come to disagree, which is the fault
 * this file exists to prevent in the first place.
 */
export const BOARD_ROW = 'grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3'

export const MASTERMIND_LAYOUT: Readonly<Record<MastermindSize, MastermindLayout>> = {
  gentle: {
    pegs: MASTERMIND_SHAPES.gentle.pegs,
    palette: MASTERMIND_SHAPES.gentle.palette,
    rowClassName: 'grid-cols-4',
    pegClassName: PEG,
    tvPegClassName: TV_PEG,
  },
  middling: {
    pegs: MASTERMIND_SHAPES.middling.pegs,
    palette: MASTERMIND_SHAPES.middling.palette,
    rowClassName: 'grid-cols-5',
    pegClassName: PEG,
    tvPegClassName: TV_PEG,
  },
  proper: {
    pegs: MASTERMIND_SHAPES.proper.pegs,
    palette: MASTERMIND_SHAPES.proper.palette,
    rowClassName: 'grid-cols-5',
    pegClassName: PEG,
    tvPegClassName: TV_PEG,
  },
}

/** How many places a row of this size has. The cursor steps along this. */
export function pegsFor(size: MastermindSize): number {
  return MASTERMIND_LAYOUT[size].pegs
}

/** Every row on screen at once: the ten she may play, and no more. */
export const ROWS_ON_SCREEN = GOES_ALLOWED

/**
 * One step along the row, stopping at each end rather than wrapping.
 *
 * Stopping rather than wrapping is Stripey's rule and Maritza's: wrapping from
 * the last place to the first looks like the cursor jumping somewhere she did
 * not ask for, and on a television that is three metres away from being
 * obvious.
 */
export function stepFrom(at: number, key: string, size: MastermindSize): number | null {
  const pegs = pegsFor(size)

  if (key === 'ArrowLeft') return at > 0 ? at - 1 : null
  if (key === 'ArrowRight') return at < pegs - 1 ? at + 1 : null

  return null
}

/**
 * Which teddy a digit picks, or null if that digit picks none.
 *
 * `count` matters for the same reason it does on the picker: pressing 7 with
 * six teddies on screen must do nothing rather than reach for one that is not
 * there, or the legend promises a key that does not work.
 */
export function teddyAtKey(key: string, count: number): number | null {
  if (!/^[1-9]$/.test(key)) return null

  const at = Number(key) - 1

  return at < count ? at : null
}
